import { createSelector } from 'reselect';

import { selectIsActive } from './attachments';
import { selectEmojiOrCommentOnBar } from './video';
import {
  SOURCE_PLAY_DASH,
  SOURCE_PLAY_HLS,
} from '../../../shared/constants/video';

export const selectVideoPlayer = state => state.video_player;

export const selectVideo = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.video
);
export const selectCurrentVideoId = createSelector(
  selectVideo,
  video => video.id
);

export const selectVideoSources = createSelector(
  selectVideo,
  video => video.sources
);
export const selectVideoViews = createSelector(
  selectVideo,
  video => video.views
);
export const selectVideoDuration = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.duration
);
export const selectAudioDuration = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.video.video_properties.audio_duration
);

export const selectVideoReactions = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.reactions
);
export const selectLocalReactions = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.local_reactions
);
export const selectAnimateReactions = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.animate_reactions
);

export const selectIsMainVideoOnSharePage = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.isMainVideoOnSharePage
);

export const selectHasStarted = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.has_started
);

export const selectPaused = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.paused
);

export const selectPlayerTime = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.current_time
);

export const selectEmojiClickedInVideoPlayer = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.emojiClickedInVideoPlayer
);

export const selectHasEnded = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.ended
);

export const selectCurrentUserIsOwner = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.video.current_user_is_owner
);

export const selectFullscreen = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.full_screen
);

export const selectStreamingSources = createSelector(
  selectVideoSources,
  sources => ({
    videoSourceHlsUrl: sources && sources[SOURCE_PLAY_HLS],
    videoSourceDashUrl: sources && sources[SOURCE_PLAY_DASH],
  })
);

export const selectPlaying = createSelector(
  selectVideoPlayer,
  videoPlayer =>
    !videoPlayer.paused && videoPlayer.has_started && !videoPlayer.ended
);

export const selectShowingComments = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.show_all_comments
);

export const selectTheaterMode = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.theater_mode
);

export const suppressPlayAnimation = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.firstPlay || videoPlayer.firstPlayAfterSeeking
);

export const selectSeeking = createSelector(
  selectVideoPlayer,
  videoPlayer => videoPlayer.seeking
);

export const selectShowCta = createSelector(
  selectHasStarted,
  selectHasEnded,
  (hasStarted, hasEnded) => hasStarted && !hasEnded
);

export const selectUploadProgressHook = createSelector(
  selectVideoPlayer,
  videoPlayer => ({
    videoId: videoPlayer.video.id,
    complete: videoPlayer.video.complete,
    sourceUrlIsTranscoded: videoPlayer.sourceUrlIsTranscoded,
    isFetchingVideoSrc: videoPlayer.is_fetching,
  })
);

export const selectUploadProgress = createSelector(
  selectVideoPlayer,
  videoPlayer => ({
    inUploadMode: videoPlayer.in_upload_mode,
    screenType: videoPlayer.video.video_properties.screen_type,
    flippedCamera: videoPlayer.video.flipped_camera,
    posterUrl: videoPlayer.video.poster_url,
    videoUploadProgress: videoPlayer.video_upload_progress,
    complete: videoPlayer.video.complete,
    videoUploadValid: videoPlayer.video.processing_information.videoUploadValid,
  })
);

export const selectIosSafariRotated = createSelector(
  selectVideoPlayer,
  videoPlayer => ({
    sourceUrlIsTranscoded: videoPlayer.sourceUrlIsTranscoded,
    mediaMetadataRotation:
      videoPlayer.video.video_properties.mediaMetadataRotation,
  })
);

export const selectControlBarExpanded = createSelector(
  state => ({
    ...selectVideoPlayer(state),
    inTrimMode: state.trimming.in_trim_mode,
    isViewerInsightsActive: selectIsActive(state),
  }),
  ({
    controlsActive,
    inTrimMode,
    isViewerInsightsActive,
    in_comment_mode: inCommentMode,
  }) =>
    Boolean(
      controlsActive || inTrimMode || isViewerInsightsActive || inCommentMode
    )
);

export const selectShowEmojiBarInsidePlayer = createSelector(
  state => ({
    ...selectVideoPlayer(state),
    inTrimMode: state.trimming.in_trim_mode,
    isViewerInsightsActive: selectIsActive(state),
    emojiOrCommentOnBar: selectEmojiOrCommentOnBar(state),
    fullScreen: selectVideoPlayer(state).full_screen,
  }),
  ({
    inTrimMode,
    emojiOrCommentOnBar,
    isMainVideoOnSharePage,
    fullScreen,
    ended,
  }) =>
    Boolean(
      (!inTrimMode &&
        emojiOrCommentOnBar &&
        isMainVideoOnSharePage &&
        fullScreen) ||
        (!isMainVideoOnSharePage && !ended && emojiOrCommentOnBar)
    )
);
